// The VPS's own numbers (Lj, Oct 11: "the VPS project could have metrics for life — how much memory we're using, how
// much storage is left, uptime, etc. — without eating all the bandwidth and RAM on our server").
//
// IN  `server/now` = orgs/{org}/opsServer/now, written by the suite API (battlebooks services/api/src/server/opsServer.ts)
//     from one host sample every 5 min (deploy/server-metrics.sh). Owners only; synced like the CFO doc — no polling
//     beyond the normal sync. Shape: { at, host, mem: { total, avail, swapTotal, swapFree } (bytes), disks: [{ mount,
//     size, avail }], load: [1, 5, 15 min], cores, uptimeSec, db: { bytes }, containers: [{ name, state, health,
//     restart }], backup: { nightly: { at, bytes, name, line }, offsite: { at, bytes, line } }, api / caddy: { ok, ms,
//     code }, projectId, hist: { step, end, mem: [%], load: [load1 × 100] } (oldest first, null = missed slot) }.
// OUT serverView(doc, now) — everything the Server card, the app card's one line and Needs you show. Pure.

const MIN = 60000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
export const SERVER_STALE_MIN = 15;          // three missed samples
export const SERVER_DISK_RED_PCT = 10;       // free below this = red (Lj: "red state when disk < 10%")
export const SERVER_DISK_WARN_PCT = 20;
export const SERVER_BACKUP_OLD_DAYS = 8;     // the weekly offsite copy missed one week

const n = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const pct = (part, whole) => (n(part) != null && n(whole) ? Math.max(0, Math.min(100, Math.round((part / whole) * 100))) : null);

/** 1536 → "1.5 KB"; bytes in powers of 1024, one decimal under 10. */
export function formatBytes(b) {
  const v = n(b);
  if (v == null) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let x = v; let i = 0;
  while (x >= 1024 && i < u.length - 1) { x /= 1024; i += 1; }
  return `${i && x < 10 ? x.toFixed(1) : Math.round(x)} ${u[i]}`;
}

/** 1036800 s → "12 d"; under a day "5 h", under an hour "40 m". */
export function formatUptime(sec) {
  const s = n(sec);
  if (s == null) return '—';
  if (s >= 86400) return `${Math.floor(s / 86400)} d`;
  if (s >= 3600) return `${Math.floor(s / 3600)} h`;
  return `${Math.max(0, Math.floor(s / 60))} m`;
}

/** "4 min ago" / "2 h ago" / "3 d ago" / "just now". */
export function formatAgo(ms, now) {
  if (n(ms) == null) return 'never';
  const d = Math.max(0, now - ms);
  if (d < MIN) return 'just now';
  if (d < HOUR) return `${Math.floor(d / MIN)} min ago`;
  if (d < DAY) return `${Math.floor(d / HOUR)} h ago`;
  return `${Math.floor(d / DAY)} d ago`;
}

/** Is `project` the one the server doc belongs to? The doc's projectId when the API stamps one, else a project named "VPS". */
export function isServerProject(project, doc) {
  if (!project || !doc) return false;
  if (doc.projectId) return doc.projectId === project.id;
  return /^\s*vps\s*$/i.test(String(project.name || ''));
}

/** A container is down when it's meant to run (a restart policy) and isn't, or Docker says it's unhealthy. */
function containerOf(c) {
  const name = String(c?.name || c?.n || '');
  const state = String(c?.state || c?.s || '') || 'unknown';
  const health = String(c?.health || c?.h || '') || null;
  const restart = String(c?.restart || c?.r || '') || 'no';
  const expected = restart !== 'no' && restart !== '';
  const down = (expected && state !== 'running') || health === 'unhealthy';
  return { name, state, health, restart, expected, down, up: state === 'running' && health !== 'unhealthy' };
}

function backupOf(b, now, { nightly }) {
  if (!b || (n(b.at) == null && n(b.bytes) == null)) return null;
  const at = n(b.at);
  const ageDays = at != null ? (now - at) / DAY : null;
  const line = typeof b.line === 'string' ? b.line : null;
  const failed = !!line && /\b(fail|failed|error|truncated)\b/i.test(line);
  // a nightly pg_dumpall is ~60 MB; anything under 1 MB is not a dump. The nightly runs at 03:15 — 26 h is late.
  const big = n(b.bytes) == null || b.bytes >= 1024 * 1024;
  const fresh = ageDays != null && ageDays <= (nightly ? 26 / 24 : SERVER_BACKUP_OLD_DAYS);
  return { at, bytes: n(b.bytes), name: b.name || null, line, ageDays, ok: big && !failed && fresh };
}

/** The history arrays → [{ t, v }] points (nulls kept as gaps), oldest first. */
function series(hist, key, scale = 1) {
  if (!hist || !Array.isArray(hist[key]) || n(hist.end) == null || !n(hist.step)) return [];
  const arr = hist[key];
  return arr.map((v, i) => ({ t: hist.end - (arr.length - 1 - i) * hist.step, v: n(v) == null ? null : v / scale }));
}

/**
 * The Server card's view of opsServer/now. null when there's no doc (not an owner, or the collector isn't installed).
 * → { at, ageMin, ago, stale, host, mem, swap, disks, rootDisk, load, uptimeSec, uptime, db, containers, up, down,
 *     backup: { nightly, offsite, last }, api, caddy, history: { mem, load }, red, reasons, oneLine }
 */
export function serverView(doc, now = Date.now()) {
  if (!doc || typeof doc !== 'object') return null;
  const at = n(doc.at) ?? n(doc.updatedAt);
  const ageMin = at != null ? Math.max(0, Math.round((now - at) / MIN)) : null;
  const m = doc.mem || {};
  const total = n(m.total);
  const used = total != null && n(m.avail) != null ? Math.max(0, total - m.avail) : null;
  const mem = { total, used, avail: n(m.avail), pct: pct(used, total) };
  const swapUsed = n(m.swapTotal) && n(m.swapFree) != null ? Math.max(0, m.swapTotal - m.swapFree) : null;
  const swap = { total: n(m.swapTotal), used: swapUsed, pct: pct(swapUsed, m.swapTotal) };
  const disks = (Array.isArray(doc.disks) ? doc.disks : []).filter((d) => d && d.mount && n(d.size)).map((d) => {
    const freePct = pct(d.avail, d.size);
    return { mount: d.mount, size: d.size, avail: n(d.avail), used: n(d.avail) != null ? d.size - d.avail : null, freePct, usedPct: freePct == null ? null : 100 - freePct, red: freePct != null && freePct < SERVER_DISK_RED_PCT, warn: freePct != null && freePct < SERVER_DISK_WARN_PCT };
  });
  const rootDisk = disks.find((d) => d.mount === '/') || disks[0] || null;
  const l = Array.isArray(doc.load) ? doc.load : [];
  const cores = n(doc.cores) || null;
  const load = { one: n(l[0]), five: n(l[1]), fifteen: n(l[2]), cores, perCore: n(l[0]) != null && cores ? l[0] / cores : null };
  load.hot = load.perCore != null && load.perCore > 1.5;
  const containers = (Array.isArray(doc.containers) ? doc.containers : []).map(containerOf).filter((c) => c.name)
    .sort((a, b) => Number(b.down) - Number(a.down) || Number(b.expected) - Number(a.expected) || a.name.localeCompare(b.name));
  const down = containers.filter((c) => c.down);
  const nightly = backupOf(doc.backup?.nightly, now, { nightly: true });
  const offsite = backupOf(doc.backup?.offsite, now, { nightly: false });
  const last = offsite || nightly;   // the offsite copy is the one that matters; until there is one, the nightly dump
  const chk = (c) => (c && typeof c === 'object' ? { ok: c.ok === true, ms: n(c.ms), code: n(c.code) } : null);
  const api = chk(doc.api);
  const caddy = chk(doc.caddy);
  const stale = ageMin == null || ageMin > SERVER_STALE_MIN;

  const reasons = [];
  for (const d of disks) if (d.red) reasons.push({ kind: 'disk', title: `Disk ${d.mount} is ${d.usedPct}% full`, detail: `${formatBytes(d.avail)} free of ${formatBytes(d.size)}` });
  for (const c of down) reasons.push({ kind: 'container', title: `Container ${c.name} is ${c.health === 'unhealthy' ? 'unhealthy' : c.state}`, detail: `restart: ${c.restart}` });
  if (last && last.ageDays != null && last.ageDays > SERVER_BACKUP_OLD_DAYS) reasons.push({ kind: 'backup', title: `Last ${offsite ? 'offsite ' : ''}backup ${Math.floor(last.ageDays)} days ago`, detail: 'Run the VPS backup' });
  else if (!last) reasons.push({ kind: 'backup', title: 'No backup found on the server', detail: 'Run the VPS backup' });
  const alarms = [...reasons];   // Needs you: disk, a container down, the backup old
  if (api && !api.ok) reasons.push({ kind: 'api', title: 'The API isn’t answering /healthz', detail: api.code ? `HTTP ${api.code}` : 'no answer' });
  if (caddy && !caddy.ok && !(api && !api.ok)) reasons.push({ kind: 'caddy', title: 'Caddy isn’t answering for the API', detail: caddy.code ? `HTTP ${caddy.code}` : 'no answer' });

  const parts = [];
  if (mem.pct != null) parts.push(`RAM ${mem.pct}%`);
  if (rootDisk?.freePct != null) parts.push(`disk ${rootDisk.freePct}% free`);
  if (n(doc.uptimeSec) != null) parts.push(`up ${formatUptime(doc.uptimeSec)}`);

  return {
    at, ageMin, ago: formatAgo(at, now), stale, host: doc.host || null,
    mem, swap, disks, rootDisk, load,
    uptimeSec: n(doc.uptimeSec), uptime: formatUptime(doc.uptimeSec),
    db: { bytes: n(doc.db?.bytes) },
    containers, up: containers.filter((c) => c.up).length, down,
    backup: { nightly, offsite, last },
    api, caddy,
    history: { mem: series(doc.hist, 'mem'), load: series(doc.hist, 'load', 100) },
    red: alarms.length > 0 || reasons.some((r) => r.kind === 'api'),
    reasons, alarms,
    oneLine: parts.join(' · '),
  };
}

/** Needs you rows (the All Home): disk under 10% free, a container down, the backup older than 8 days. */
export function serverNeeds(view, project = null) {
  if (!view) return [];
  return view.alarms.map((r) => ({ kind: `server-${r.kind}`, title: r.title, detail: r.detail, projectId: project?.id || null }));
}
